import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  GlobalStyles,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import trLocale from 'date-fns/locale/tr';
import enLocale from 'date-fns/locale/en-US';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CategoryIcon from '@mui/icons-material/Category';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { getBranches } from '../../../services/migration/branchDataService';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS } from '../shared/StatsCard';
import {
  getErrorMessage,
  getProfitLossByBranch,
  getProfitLossDataQuality,
  getProfitLossExpenseBreakdown,
  getProfitLossStatement,
  getProfitLossTrend,
  type ProfitLossByBranch as ByBranchData,
  type ProfitLossCompareMode,
  type ProfitLossDataQuality as QualityData,
  type ProfitLossExpenseBreakdown as BreakdownData,
  type ProfitLossLine,
  type ProfitLossQuery,
  type ProfitLossStatement,
  type ProfitLossTrend as TrendData,
} from './profitLossService';
import {
  formatChange,
  formatPercent,
  formatTry,
  HIDE_WHEN_EMPTY,
  lineText,
  monthLabel,
  toMonthKey,
  UNCATEGORIZED_KEY,
} from './profitLossFormat';
import ProfitLossDrilldownDialog from './ProfitLossDrilldownDialog';
import ProfitLossByBranch, { branchColumnName, visibleBranchRows } from './ProfitLossByBranch';
import ProfitLossTrend, { MAX_TREND_MONTHS, monthsBetween } from './ProfitLossTrend';
import ProfitLossExpenseBreakdown from './ProfitLossExpenseBreakdown';
import ProfitLossDataQuality from './ProfitLossDataQuality';
import CashExpenseCategoriesModal from '../cashbox/CashExpenseCategoriesModal';
import ReportsHubBackButton from '../../reports/hub/ReportsHubBackButton';

interface BranchOption {
  id: string;
  name: string;
}

type Preset = 'today' | 'thisMonth' | 'lastMonth' | 'thisYear';
type TabKey = 'statement' | 'branches' | 'trend' | 'breakdown' | 'quality';

interface TabData<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

const emptyTab = <T,>(): TabData<T> => ({ data: null, loading: false, error: null });

const MAX_RANGE_DAYS = 366;
const PRINT_AREA_ID = 'profit-loss-print-area';
const MONEY_FMT = '#,##0.00 "₺";[Red]-#,##0.00 "₺"';

/** Bu dönemde ve (varsa) karşılaştırma döneminde tutarı olmayan isteğe bağlı gruplar gizlenir. */
const isZeroLine = (line: ProfitLossLine): boolean =>
  line.Amount === 0 && !line.CompareAmount && line.Children.every(isZeroLine);

const isEmptyOptionalLine = (line: ProfitLossLine) => HIDE_WHEN_EMPTY.has(line.Key) && isZeroLine(line);

const presetRange = (preset: Preset): [Date, Date] => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (preset) {
    case 'today':
      return [new Date(y, m, now.getDate()), new Date(y, m, now.getDate())];
    case 'lastMonth':
      return [new Date(y, m - 1, 1), new Date(y, m, 0)];
    case 'thisYear':
      return [new Date(y, 0, 1), new Date(y, m, now.getDate())];
    case 'thisMonth':
    default:
      return [new Date(y, m, 1), new Date(y, m, now.getDate())];
  }
};

const daysBetween = (a: Date, b: Date) =>
  Math.round((new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime()
    - new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime()) / 86400000);

/** KPI karşılaştırması: önceki 0 ise yüzde yok. */
const kpiChange = (current: number, previous: number): number | null =>
  previous === 0 ? null : Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;

interface ProfitLossProps {
  /** Şube yöneticisi görünümü: şube sabit, merkez/banka/kart/finansman satırları sunucuda dışlanır. */
  isManager?: boolean;
  managerBranchId?: string;
}

const ProfitLoss: React.FC<ProfitLossProps> = ({ isManager = false, managerBranchId }) => {
  const { t, i18n } = useTranslation('accounting');
  const { userDetails } = useAuth();
  const companyId = userDetails?.companyId as string | undefined;
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'tr-TR';
  const managerMode = isManager && !!managerBranchId;

  const [initialStart, initialEnd] = presetRange('thisMonth');
  const [startDate, setStartDate] = useState<Date | null>(initialStart);
  const [endDate, setEndDate] = useState<Date | null>(initialEnd);
  const [branches, setBranches] = useState<BranchOption[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<BranchOption[]>(
    managerMode ? [{ id: managerBranchId!, name: '' }] : [],
  );

  const [tab, setTab] = useState<TabKey>('statement');
  const [compareMode, setCompareMode] = useState<ProfitLossCompareMode>('none');
  const [statement, setStatement] = useState<ProfitLossStatement | null>(null);
  const [lastQuery, setLastQuery] = useState<ProfitLossQuery | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [drill, setDrill] = useState<{ key: string; label: string } | null>(null);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const [byBranch, setByBranch] = useState<TabData<ByBranchData>>(emptyTab);
  const [trend, setTrend] = useState<TabData<TrendData>>(emptyTab);
  const [breakdown, setBreakdown] = useState<TabData<BreakdownData>>(emptyTab);
  const [quality, setQuality] = useState<TabData<QualityData>>(emptyTab);
  const [trendStart, setTrendStart] = useState<Date | null>(
    new Date(initialEnd.getFullYear(), initialEnd.getMonth() - (MAX_TREND_MONTHS - 1), 1),
  );
  const [trendEnd, setTrendEnd] = useState<Date | null>(new Date(initialEnd.getFullYear(), initialEnd.getMonth(), 1));

  useEffect(() => {
    if (!companyId) return;
    getBranches(companyId)
      .then(list => {
        const options = (list || [])
          .filter(b => b.name)
          .map(b => ({ id: String(b.id), name: b.name }))
          .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
        if (managerMode) {
          const own = options.find(o => o.id === managerBranchId)
            ?? { id: managerBranchId!, name: t('profitLoss.manager.ownBranch') };
          setBranches([own]);
          setSelectedBranches([own]);
        } else {
          setBranches(options);
        }
      })
      .catch(() => setBranches([]));
  }, [companyId, managerMode, managerBranchId, t]);

  const rangeError = useMemo(() => {
    if (!startDate || !endDate) return t('profitLoss.errors.dateRequired');
    if (endDate < startDate) return t('profitLoss.errors.endBeforeStart');
    if (daysBetween(startDate, endDate) > MAX_RANGE_DAYS) return t('profitLoss.errors.rangeTooLong', { days: MAX_RANGE_DAYS });
    return null;
  }, [startDate, endDate, t]);

  const trendMonthError = useMemo(() => {
    if (!trendStart || !trendEnd) return t('profitLoss.errors.dateRequired');
    if (monthsBetween(trendStart, trendEnd) < 1) return t('profitLoss.errors.endBeforeStart');
    if (monthsBetween(trendStart, trendEnd) > MAX_TREND_MONTHS) {
      return t('profitLoss.trend.tooLong', { months: MAX_TREND_MONTHS });
    }
    return null;
  }, [trendStart, trendEnd, t]);

  const lineLabel = (line: ProfitLossLine) => lineText(t, line.Key, line.Label);

  const applyPreset = (preset: Preset) => {
    const [s, e] = presetRange(preset);
    setStartDate(s);
    setEndDate(e);
  };

  const filterQuery = (): ProfitLossQuery | null => {
    if (rangeError || !startDate || !endDate) return null;
    return { startDate, endDate, branchIds: selectedBranches.map(b => b.id) };
  };

  const runTab = async <T,>(
    setter: React.Dispatch<React.SetStateAction<TabData<T>>>,
    load: () => Promise<T>,
  ) => {
    setter(prev => ({ ...prev, loading: true, error: null }));
    try {
      const data = await load();
      setter({ data, loading: false, error: null });
    } catch (err) {
      setter(prev => ({ ...prev, loading: false, error: getErrorMessage(err, t('profitLoss.errors.loadFailed')) }));
    }
  };

  /** Sekme butonu başlıktaki (son oluşturulan) dönemi kullanır; henüz rapor yoksa filtreleri. */
  const tabQuery = () => lastQuery ?? filterQuery();

  const loadByBranch = (q = tabQuery()) => { if (q) runTab(setByBranch, () => getProfitLossByBranch(q, managerMode)); };
  const loadBreakdown = (q = tabQuery()) => { if (q) runTab(setBreakdown, () => getProfitLossExpenseBreakdown(q)); };
  const loadQuality = (q = tabQuery()) => { if (q) runTab(setQuality, () => getProfitLossDataQuality(q)); };
  const loadTrend = () => {
    if (trendMonthError || !trendStart || !trendEnd) return;
    const ids = selectedBranches.map(b => b.id);
    runTab(setTrend, () => getProfitLossTrend(toMonthKey(trendStart), toMonthKey(trendEnd), ids));
  };

  const loadStatement = async (query: ProfitLossQuery, mode: ProfitLossCompareMode) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getProfitLossStatement(query, mode);
      setStatement(data);
      setLastQuery(query);
      setExpanded(prev => (Object.keys(prev).length > 0 ? prev : { revenue: true, opex: true }));
    } catch (err) {
      setError(getErrorMessage(err, t('profitLoss.errors.loadFailed')));
    } finally {
      setLoading(false);
    }
  };

  /** Ana buton: Gelir Tablosu ve açık sekme yeniden hesaplanır, diğer sekmelerin eski verisi temizlenir. */
  const generate = async () => {
    const query = filterQuery();
    if (!query) return;
    setByBranch(emptyTab);
    setBreakdown(emptyTab);
    setQuality(emptyTab);
    if (tab === 'branches') loadByBranch(query);
    if (tab === 'breakdown') loadBreakdown(query);
    if (tab === 'quality') loadQuality(query);
    if (tab === 'trend') loadTrend();
    await loadStatement(query, compareMode);
  };

  const changeCompareMode = (mode: ProfitLossCompareMode) => {
    setCompareMode(mode);
    if (lastQuery) loadStatement(lastQuery, mode);
  };

  const toggle = (key: string) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const selectedBranchesLabel = (ids: string[]) => {
    if (ids.length === 0) return t('profitLoss.allBranches');
    return ids.map(id => branches.find(b => b.id === id)?.name ?? id).join(', ');
  };

  const warningText = (code: string, fallback: string) =>
    t(`profitLoss.warnings.${code}`, { defaultValue: fallback });

  const compareActive = !!statement && !!statement.CompareMode && statement.CompareMode !== 'none';
  const periodText = (q: ProfitLossQuery) =>
    `${q.startDate.toLocaleDateString(locale)} - ${q.endDate.toLocaleDateString(locale)}`;
  const comparePeriodText = statement?.ComparePeriodStart && statement?.ComparePeriodEnd
    ? `${new Date(statement.ComparePeriodStart).toLocaleDateString(locale)} - ${new Date(statement.ComparePeriodEnd).toLocaleDateString(locale)}`
    : '';

  const warningBadge = quality.data
    ? quality.data.Items.filter(i => i.Severity === 'warning').length
    : (statement?.Warnings.filter(w => w.Code !== 'posCancelled').length ?? 0);

  // ---------------- Excel ----------------

  const styleHeader = (row: ExcelJS.Row) => {
    row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    row.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } };
    });
  };

  const shadeSubtotal = (row: ExcelJS.Row) => {
    row.font = { bold: true };
    row.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
    });
  };

  const addTitle = (sheet: ExcelJS.Worksheet, title: string, period: string, branchLabel?: string) => {
    sheet.addRow([title]).font = { bold: true, size: 14 };
    sheet.addRow([period]);
    if (branchLabel) sheet.addRow([branchLabel]);
    sheet.addRow([t('profitLoss.vatNote')]).font = { italic: true };
    sheet.addRow([]);
  };

  const writeStatementSheet = (workbook: ExcelJS.Workbook, st: ProfitLossStatement, q: ProfitLossQuery) => {
    const sheet = workbook.addWorksheet(t('profitLoss.excel.sheetName'));
    addTitle(sheet, t('profitLoss.title'), periodText(q), selectedBranchesLabel(q.branchIds));
    if (compareActive) {
      sheet.addRow([`${t(`profitLoss.compare.modes.${st.CompareMode}`)}: ${comparePeriodText}`]);
      sheet.addRow([]);
    }

    styleHeader(sheet.addRow([
      t('profitLoss.table.line'),
      t('profitLoss.table.amount'),
      t('profitLoss.table.percent'),
      ...(compareActive ? [t('profitLoss.compare.previous'), t('profitLoss.compare.change')] : []),
    ]));

    const writeLine = (line: ProfitLossLine, depth: number) => {
      if (isEmptyOptionalLine(line)) return;
      const row = sheet.addRow([
        `${'    '.repeat(depth)}${lineLabel(line)}`,
        line.Amount,
        line.PercentOfNetSales === null ? '' : line.PercentOfNetSales / 100,
        ...(compareActive
          ? [line.CompareAmount ?? 0, line.ChangePercent === null || line.ChangePercent === undefined ? '' : line.ChangePercent / 100]
          : []),
      ]);
      row.getCell(2).numFmt = MONEY_FMT;
      row.getCell(3).numFmt = '0.00%';
      if (compareActive) {
        row.getCell(4).numFmt = MONEY_FMT;
        row.getCell(5).numFmt = '+0.0%;-0.0%;0.0%';
      }
      if (depth === 0) row.font = { bold: true };
      if (line.IsSubtotal) shadeSubtotal(row);
      line.Children.forEach(child => writeLine(child, depth + 1));
    };
    st.Lines.forEach(line => writeLine(line, 0));

    if (st.Warnings.length > 0) {
      sheet.addRow([]);
      sheet.addRow([t('profitLoss.warningsTitle')]).font = { bold: true };
      st.Warnings.forEach(w => {
        sheet.addRow([warningText(w.Code, w.Message), w.Amount ?? '']).getCell(2).numFmt = MONEY_FMT;
      });
    }

    sheet.getColumn(1).width = 48;
    [2, 4].forEach(c => { sheet.getColumn(c).width = 20; });
    [3, 5].forEach(c => { sheet.getColumn(c).width = 14; });
  };

  const writeBranchSheet = (workbook: ExcelJS.Workbook, data: ByBranchData, q: ProfitLossQuery) => {
    const sheet = workbook.addWorksheet(t('profitLoss.tabs.branches'));
    addTitle(sheet, `${t('profitLoss.title')} - ${t('profitLoss.tabs.branches')}`, periodText(q));
    styleHeader(sheet.addRow([t('profitLoss.table.line'), ...data.Columns.map(c => branchColumnName(c, t))]));
    visibleBranchRows(data.Rows).forEach(r => {
      const row = sheet.addRow([`${'    '.repeat(r.Depth)}${lineText(t, r.Key, r.Label)}`, ...r.Values.map(v => v.Amount)]);
      r.Values.forEach((_, i) => { row.getCell(i + 2).numFmt = MONEY_FMT; });
      if (r.Depth === 0) row.font = { bold: true };
      if (r.IsSubtotal) shadeSubtotal(row);
    });
    sheet.getColumn(1).width = 44;
    data.Columns.forEach((_, i) => { sheet.getColumn(i + 2).width = 18; });
  };

  const writeTrendSheet = (workbook: ExcelJS.Workbook, data: TrendData) => {
    const sheet = workbook.addWorksheet(t('profitLoss.tabs.trend'));
    addTitle(
      sheet,
      `${t('profitLoss.title')} - ${t('profitLoss.tabs.trend')}`,
      `${monthLabel(data.StartMonth, locale)} - ${monthLabel(data.EndMonth, locale)}`,
      selectedBranchesLabel(data.BranchIds ?? []),
    );
    styleHeader(sheet.addRow([
      t('profitLoss.trend.month'),
      t('profitLoss.kpis.netSales'),
      t('profitLoss.trend.cogs'),
      t('profitLoss.kpis.grossProfit'),
      t('profitLoss.trend.grossMargin'),
      t('profitLoss.kpis.operatingExpenses'),
      t('profitLoss.kpis.netProfit'),
      t('profitLoss.trend.netMargin'),
    ]));
    data.Points.forEach(p => {
      const row = sheet.addRow([
        monthLabel(p.Month, locale),
        p.NetSales,
        p.Cogs,
        p.GrossProfit,
        p.GrossMarginPercent === null ? '' : p.GrossMarginPercent / 100,
        p.OperatingExpenses,
        p.NetProfit,
        p.NetMarginPercent === null ? '' : p.NetMarginPercent / 100,
      ]);
      [2, 3, 4, 6, 7].forEach(c => { row.getCell(c).numFmt = MONEY_FMT; });
      [5, 8].forEach(c => { row.getCell(c).numFmt = '0.00%'; });
    });
    sheet.getColumn(1).width = 16;
    for (let c = 2; c <= 8; c += 1) sheet.getColumn(c).width = 18;
  };

  const writeBreakdownSheet = (workbook: ExcelJS.Workbook, data: BreakdownData, q: ProfitLossQuery) => {
    const sheet = workbook.addWorksheet(t('profitLoss.tabs.breakdown'));
    addTitle(sheet, `${t('profitLoss.title')} - ${t('profitLoss.tabs.breakdown')}`, periodText(q), selectedBranchesLabel(q.branchIds));
    sheet.addRow([t('profitLoss.breakdown.groupsTitle')]).font = { bold: true };
    styleHeader(sheet.addRow([
      t('profitLoss.breakdown.group'), t('profitLoss.table.amount'),
      t('profitLoss.breakdown.percentOfExpenses'), t('profitLoss.table.percent'),
    ]));
    data.Groups.forEach(g => {
      const row = sheet.addRow([
        lineText(t, g.Key, g.Label), g.Amount,
        g.PercentOfExpenses === null ? '' : g.PercentOfExpenses / 100,
        g.PercentOfNetSales === null ? '' : g.PercentOfNetSales / 100,
      ]);
      row.getCell(2).numFmt = MONEY_FMT;
      row.getCell(3).numFmt = '0.00%';
      row.getCell(4).numFmt = '0.00%';
    });
    const totalRow = sheet.addRow([t('profitLoss.breakdown.totalExpenses'), data.TotalExpenses]);
    totalRow.getCell(2).numFmt = MONEY_FMT;
    shadeSubtotal(totalRow);

    sheet.addRow([]);
    sheet.addRow([t('profitLoss.breakdown.topTitle')]).font = { bold: true };
    styleHeader(sheet.addRow([
      t('profitLoss.table.line'), t('profitLoss.table.amount'),
      t('profitLoss.breakdown.percentOfExpenses'), t('profitLoss.table.percent'), t('profitLoss.breakdown.group'),
    ]));
    data.TopItems.forEach(item => {
      const row = sheet.addRow([
        lineText(t, item.Key, item.Label), item.Amount,
        item.PercentOfExpenses === null ? '' : item.PercentOfExpenses / 100,
        item.PercentOfNetSales === null ? '' : item.PercentOfNetSales / 100,
        lineText(t, item.GroupKey, item.GroupLabel),
      ]);
      row.getCell(2).numFmt = MONEY_FMT;
      row.getCell(3).numFmt = '0.00%';
      row.getCell(4).numFmt = '0.00%';
    });
    sheet.getColumn(1).width = 40;
    sheet.getColumn(2).width = 20;
    sheet.getColumn(3).width = 14;
    sheet.getColumn(4).width = 14;
    sheet.getColumn(5).width = 28;
  };

  const writeQualitySheet = (workbook: ExcelJS.Workbook, data: QualityData, q: ProfitLossQuery) => {
    const sheet = workbook.addWorksheet(t('profitLoss.tabs.quality'));
    addTitle(sheet, `${t('profitLoss.title')} - ${t('profitLoss.tabs.quality')}`, periodText(q), selectedBranchesLabel(q.branchIds));
    styleHeader(sheet.addRow([
      t('profitLoss.dataQuality.issue'), t('profitLoss.dataQuality.count'),
      t('profitLoss.table.amount'), t('profitLoss.dataQuality.detail'),
    ]));
    if (data.Items.length === 0) sheet.addRow([t('profitLoss.dataQuality.clean')]);
    data.Items.forEach(item => {
      const row = sheet.addRow([
        t(`profitLoss.dataQuality.titles.${item.Code}`, { defaultValue: item.Code }),
        item.Count,
        item.Amount ?? '',
        t(`profitLoss.dataQuality.messages.${item.Code}`, { defaultValue: item.Message }),
      ]);
      if (!item.Code.endsWith('WithoutRate')) row.getCell(3).numFmt = MONEY_FMT;
    });
    sheet.getColumn(1).width = 40;
    sheet.getColumn(2).width = 10;
    sheet.getColumn(3).width = 20;
    sheet.getColumn(4).width = 90;
  };

  /** Her yüklü sekme ayrı çalışma sayfası. */
  const exportExcel = async () => {
    if (!statement || !lastQuery) return;
    const workbook = new ExcelJS.Workbook();
    writeStatementSheet(workbook, statement, lastQuery);
    if (byBranch.data) writeBranchSheet(workbook, byBranch.data, lastQuery);
    if (trend.data) writeTrendSheet(workbook, trend.data);
    if (breakdown.data) writeBreakdownSheet(workbook, breakdown.data, lastQuery);
    if (quality.data) writeQualitySheet(workbook, quality.data, lastQuery);

    const buffer = await workbook.xlsx.writeBuffer();
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    saveAs(new Blob([buffer]), `${t('profitLoss.excel.filePrefix')}_${stamp}.xlsx`);
  };

  // ---------------- PDF ----------------

  /** Gelir Tablosu ve KPI'lar; ekrandaki açık/kapalı satırlarla aynı görünüm. */
  const exportPdf = async () => {
    if (!statement) return;
    setPdfBusy(true);
    try {
      if (tab !== 'statement') {
        setTab('statement');
        await new Promise(resolve => setTimeout(resolve, 400));
      }
      const el = document.getElementById(PRINT_AREA_ID);
      if (!el) return;
      const canvas = await html2canvas(el, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: '#ffffff',
        ignoreElements: node => node.classList?.contains('pl-no-print') ?? false,
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const margin = 8;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const contentWidth = pageWidth - margin * 2;
      const contentHeight = pageHeight - margin * 2;
      const pxPerMm = canvas.width / contentWidth;
      const slicePx = Math.floor(contentHeight * pxPerMm);

      for (let y = 0, page = 0; y < canvas.height; y += slicePx, page += 1) {
        const h = Math.min(slicePx, canvas.height - y);
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = h;
        slice.getContext('2d')?.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
        if (page > 0) pdf.addPage();
        pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', margin, margin, contentWidth, h / pxPerMm);
      }

      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      pdf.save(`${t('profitLoss.excel.filePrefix')}_${stamp}.pdf`);
    } catch (err) {
      setError(getErrorMessage(err, t('profitLoss.pdfFailed')));
    } finally {
      setPdfBusy(false);
    }
  };

  // ---------------- Gelir Tablosu ----------------

  const renderChange = (line: ProfitLossLine) => {
    const previous = line.CompareAmount ?? 0;
    const delta = line.Amount - previous;
    const color = delta === 0 || line.ChangePercent === null || line.ChangePercent === undefined
      ? 'text.secondary'
      : delta > 0 ? 'success.main' : 'error.main';
    return (
      <TableCell align="right" sx={{ whiteSpace: 'nowrap', color }}>
        {formatChange(line.ChangePercent)}
      </TableCell>
    );
  };

  const renderLine = (line: ProfitLossLine, depth: number): React.ReactNode => {
    if (isEmptyOptionalLine(line)) return null;
    const hasChildren = line.Children.length > 0;
    const flagged = line.Key === UNCATEGORIZED_KEY && line.Amount !== 0;
    const isOpen = !!expanded[line.Key];
    const canDrill = line.DrilldownAvailable && line.Amount !== 0;
    const label = lineLabel(line);

    return (
      <React.Fragment key={line.Key}>
        <TableRow
          hover={canDrill}
          onClick={canDrill ? () => setDrill({ key: line.Key, label }) : undefined}
          sx={{
            cursor: canDrill ? 'pointer' : 'default',
            backgroundColor: line.IsSubtotal ? 'action.hover' : undefined,
            '& td': { fontWeight: line.IsSubtotal || depth === 0 ? 700 : 400 },
          }}
        >
          <TableCell sx={{ pl: 1 + depth * 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {hasChildren ? (
                <IconButton
                  size="small"
                  className="pl-no-print"
                  onClick={e => { e.stopPropagation(); toggle(line.Key); }}
                  sx={{ mr: 0.5 }}
                >
                  {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
              ) : (
                <Box sx={{ width: 34 }} />
              )}
              {canDrill ? (
                <Tooltip title={t('profitLoss.table.drillHint')}>
                  <span>{label}</span>
                </Tooltip>
              ) : label}
              {flagged && (
                <Tooltip title={t('profitLoss.uncategorizedHint')}>
                  <WarningAmberIcon fontSize="small" color="warning" sx={{ ml: 0.75 }} />
                </Tooltip>
              )}
            </Box>
          </TableCell>
          <TableCell
            align="right"
            sx={{ whiteSpace: 'nowrap', color: line.Amount < 0 ? 'error.main' : 'inherit' }}
          >
            {formatTry(line.Amount)}
          </TableCell>
          <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
            {formatPercent(line.PercentOfNetSales)}
          </TableCell>
          {compareActive && (
            <>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                {formatTry(line.CompareAmount ?? 0)}
              </TableCell>
              {renderChange(line)}
            </>
          )}
        </TableRow>
        {hasChildren && isOpen && line.Children.map(child => renderLine(child, depth + 1))}
      </React.Fragment>
    );
  };

  const kpis = statement?.Kpis;
  const prevKpis = compareActive ? statement?.CompareKpis ?? null : null;
  const dateLocale = i18n.language?.startsWith('en') ? enLocale : trLocale;

  const kpiSubtitle = (label: string, current: number, previous: number | undefined) => {
    if (!prevKpis || previous === undefined) return label;
    return `${label} · ${t('profitLoss.compare.previous')}: ${formatTry(previous)} (${formatChange(kpiChange(current, previous))})`;
  };

  const renderStatementTab = () => statement && kpis && (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }} className="pl-no-print">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('profitLoss.compare.label')}</InputLabel>
          <Select
            label={t('profitLoss.compare.label')}
            value={compareMode}
            onChange={e => changeCompareMode(e.target.value as ProfitLossCompareMode)}
            disabled={loading}
          >
            {(['none', 'previous', 'lastYear'] as ProfitLossCompareMode[]).map(m => (
              <MenuItem key={m} value={m}>{t(`profitLoss.compare.modes.${m}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {loading && <CircularProgress size={20} />}
      </Stack>
      {compareActive && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t(`profitLoss.compare.modes.${statement.CompareMode}`)}: {comparePeriodText}
        </Typography>
      )}

      <StatsCardsContainer cardCount={5}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatsCardItem
            icon={<TrendingUpIcon />}
            color={STATS_COLORS.blue}
            title={formatTry(kpis.NetSales)}
            subtitle={kpiSubtitle(t('profitLoss.kpis.netSales'), kpis.NetSales, prevKpis?.NetSales)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatsCardItem
            icon={<AccountBalanceWalletIcon />}
            color={kpis.GrossProfit >= 0 ? STATS_COLORS.green : STATS_COLORS.red}
            title={formatTry(kpis.GrossProfit)}
            subtitle={kpiSubtitle(
              `${t('profitLoss.kpis.grossProfit')} (${formatPercent(kpis.GrossMarginPercent)})`,
              kpis.GrossProfit,
              prevKpis?.GrossProfit,
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatsCardItem
            icon={<TrendingDownIcon />}
            color={STATS_COLORS.orange}
            title={formatTry(kpis.OperatingExpenses)}
            subtitle={kpiSubtitle(t('profitLoss.kpis.operatingExpenses'), kpis.OperatingExpenses, prevKpis?.OperatingExpenses)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatsCardItem
            icon={<AssessmentIcon />}
            color={kpis.NetProfit >= 0 ? STATS_COLORS.green : STATS_COLORS.red}
            title={formatTry(kpis.NetProfit)}
            subtitle={kpiSubtitle(
              `${t('profitLoss.kpis.netProfit')} (${formatPercent(kpis.NetMarginPercent)})`,
              kpis.NetProfit,
              prevKpis?.NetProfit,
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatsCardItem
            icon={<RestaurantIcon />}
            color={STATS_COLORS.purple}
            title={formatPercent(kpis.PrimeCostPercent)}
            subtitle={prevKpis
              ? `${t('profitLoss.kpis.primeCost')} · ${t('profitLoss.compare.previous')}: ${formatPercent(prevKpis.PrimeCostPercent)}`
              : t('profitLoss.kpis.primeCost')}
          />
        </Grid>
      </StatsCardsContainer>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>{t('profitLoss.table.line')}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{t('profitLoss.table.amount')}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{t('profitLoss.table.percent')}</TableCell>
              {compareActive && (
                <>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{t('profitLoss.compare.previous')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{t('profitLoss.compare.change')}</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {statement.Lines.map(line => renderLine(line, 0))}
          </TableBody>
        </Table>
      </TableContainer>

      {statement.Warnings.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('profitLoss.warningsTitle')}</Typography>
          <Stack spacing={1}>
            {statement.Warnings.map(w => (
              <Alert key={w.Code} severity={w.Code === 'posCancelled' ? 'info' : 'warning'}>
                {warningText(w.Code, w.Message)}
                {w.Amount !== null && ` ${formatTry(w.Amount)}`}
                {w.Count !== null && ` (${t('profitLoss.recordCount', { count: w.Count })})`}
              </Alert>
            ))}
          </Stack>
        </Box>
      )}
    </>
  );

  const canGenerateTab = !!lastQuery || !rangeError;

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <GlobalStyles styles={{
        '@media print': {
          'body *': { visibility: 'hidden' },
          [`#${PRINT_AREA_ID}, #${PRINT_AREA_ID} *`]: { visibility: 'visible' },
          [`#${PRINT_AREA_ID}`]: { position: 'absolute', left: 0, top: 0, width: '100%' },
          '.pl-no-print': { display: 'none !important' },
        },
      }} />

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        {!managerMode && <ReportsHubBackButton reportPath="/kar-zarar" />}
        <AssessmentIcon color="primary" />
        <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>{t('profitLoss.title')}</Typography>
        {!managerMode && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<CategoryIcon />}
            onClick={() => setCategoriesOpen(true)}
            className="pl-no-print"
          >
            {t('profitLoss.expenseCategories')}
          </Button>
        )}
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }} className="pl-no-print">
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label={t('profitLoss.startDate')}
                value={startDate}
                onChange={v => setStartDate(v)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label={t('profitLoss.endDate')}
                value={endDate}
                onChange={v => setEndDate(v)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {(['today', 'thisMonth', 'lastMonth', 'thisYear'] as Preset[]).map(p => (
                  <Chip key={p} label={t(`profitLoss.presets.${p}`)} size="small" onClick={() => applyPreset(p)} />
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={branches}
                value={selectedBranches}
                onChange={(_, v) => setSelectedBranches(v)}
                disabled={managerMode}
                disableClearable={managerMode}
                getOptionLabel={o => o.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      checked={selected}
                      sx={{ mr: 1 }}
                    />
                    {option.name}
                  </li>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('profitLoss.branches')}
                    placeholder={selectedBranches.length === 0 ? t('profitLoss.allBranches') : undefined}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={generate}
                disabled={loading || !!rangeError}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AssessmentIcon />}
              >
                {loading ? t('profitLoss.generating') : t('profitLoss.generate')}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
        {rangeError && startDate && endDate && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>{rangeError}</Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('profitLoss.vatNote')}
        </Typography>
        {managerMode && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('profitLoss.manager.note')}
          </Typography>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} className="pl-no-print">{error}</Alert>}

      <Paper sx={{ mb: 2 }} className="pl-no-print">
        <Tabs
          value={tab}
          onChange={(_, v: TabKey) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="statement" label={t('profitLoss.tabs.statement')} />
          <Tab value="branches" label={t('profitLoss.tabs.branches')} />
          <Tab value="trend" label={t('profitLoss.tabs.trend')} />
          <Tab value="breakdown" label={t('profitLoss.tabs.breakdown')} />
          <Tab
            value="quality"
            label={(
              <Badge color="warning" badgeContent={warningBadge} invisible={warningBadge === 0} sx={{ pr: warningBadge > 0 ? 1.5 : 0 }}>
                {t('profitLoss.tabs.quality')}
              </Badge>
            )}
          />
        </Tabs>
      </Paper>

      <Box id={PRINT_AREA_ID}>
        {lastQuery && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ mb: 2 }}
            spacing={1}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('profitLoss.title')} · {periodText(lastQuery)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tab === 'branches' && !managerMode ? t('profitLoss.allBranches') : selectedBranchesLabel(lastQuery.branchIds)}
                {tab !== 'branches' && statement?.IncludesUnbranched ? ` · ${t('profitLoss.includesUnbranched')}` : ''}
                {managerMode ? ` · ${t('profitLoss.manager.short')}` : ''}
                {' · '}
                {t('profitLoss.vatShort')}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} className="pl-no-print">
              <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportExcel} disabled={!statement}>
                {t('profitLoss.exportExcel')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={pdfBusy ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                onClick={exportPdf}
                disabled={!statement || pdfBusy}
              >
                {t('profitLoss.exportPdf')}
              </Button>
              <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
                {t('profitLoss.print')}
              </Button>
            </Stack>
          </Stack>
        )}

        {tab === 'statement' && (
          statement ? renderStatementTab() : (!loading && (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('profitLoss.emptyHint')}</Typography>
            </Paper>
          ))
        )}
        {tab === 'statement' && loading && !statement && (
          <Paper sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={32} /></Paper>
        )}

        {tab === 'branches' && (
          <ProfitLossByBranch
            data={byBranch.data}
            loading={byBranch.loading}
            error={byBranch.error}
            canGenerate={canGenerateTab}
            onGenerate={() => loadByBranch()}
            managerMode={managerMode}
          />
        )}
        {tab === 'trend' && (
          <ProfitLossTrend
            data={trend.data}
            loading={trend.loading}
            error={trend.error}
            startMonth={trendStart}
            endMonth={trendEnd}
            onStartMonthChange={setTrendStart}
            onEndMonthChange={setTrendEnd}
            monthError={trendMonthError}
            onGenerate={loadTrend}
          />
        )}
        {tab === 'breakdown' && (
          <ProfitLossExpenseBreakdown
            data={breakdown.data}
            loading={breakdown.loading}
            error={breakdown.error}
            canGenerate={canGenerateTab}
            onGenerate={() => loadBreakdown()}
          />
        )}
        {tab === 'quality' && (
          <ProfitLossDataQuality
            data={quality.data}
            loading={quality.loading}
            error={quality.error}
            canGenerate={canGenerateTab}
            onGenerate={() => loadQuality()}
            navigable={!managerMode}
          />
        )}
      </Box>

      <ProfitLossDrilldownDialog
        open={!!drill}
        lineKey={drill?.key ?? null}
        lineLabel={drill?.label ?? ''}
        query={lastQuery}
        onClose={() => setDrill(null)}
      />

      {!managerMode && (
        <CashExpenseCategoriesModal
          open={categoriesOpen}
          onClose={() => {
            setCategoriesOpen(false);
            // Tür grubu değiştiyse tablo yeni gruplamayla yeniden hesaplanır.
            if (statement) generate();
          }}
        />
      )}
    </Box>
  );
};

export default ProfitLoss;
